import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCourse } from '../../services/courses';
import CoursePreviewExperience from '../../components/courses/CoursePreviewExperience';
import CommentsSection from '../../components/comments/CommentsSection';

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/**
 * CoursePreviewPage — `/courses/:id/preview`. Renders the course exactly as a
 * learner would experience it: the immersive course-player shell (no admin
 * sidebar) stepping through one module at a time via CoursePreviewPlayer, which
 * reuses the canonical `components/modules/views`. A slim top bar lets the
 * author exit back to where they came from.
 */
interface ProgramChainState {
  programId: string;
  dayIds: string[];
  dayIndex: number;
  courseIds: string[];
  courseIndex: number;
}

export default function CoursePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: course, isLoading, isError } = useCourse(id);

  // When opened from a program-day preview, the chain lets the author flow
  // course → course → next day without bouncing off the tile pages.
  const chain = (location.state as { programChain?: ProgramChainState } | null)
    ?.programChain;
  const nextCourseId = chain ? chain.courseIds[chain.courseIndex + 1] : undefined;
  const nextDayId = chain ? chain.dayIds[chain.dayIndex + 1] : undefined;
  const advance = () => {
    if (!chain) return;
    if (nextCourseId) {
      navigate(`/courses/${nextCourseId}/preview`, {
        replace: true,
        state: { programChain: { ...chain, courseIndex: chain.courseIndex + 1 } },
      });
    } else if (nextDayId) {
      navigate(`/programs/${chain.programId}/preview/days/${nextDayId}`);
    } else {
      navigate(`/programs/${chain.programId}/preview/days`);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Author exit bar — sticky at the very top of the immersive shell. */}
      <div className="sticky top-[var(--sticky-top,0px)] z-30 px-4 sm:px-6 py-3 bg-background-primary/85 backdrop-blur-md border-b border-border-primary/15">
        <div className="max-w-[1080px] mx-auto flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            ← Back
          </button>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-350 text-xs font-bold shrink-0">
            <EyeIcon />
            Previewing as learner
          </span>
          <h1 className="text-lg font-semibold text-text-primary truncate">
            {course?.title ?? 'Loading…'}
          </h1>
          {chain && (
            <button
              type="button"
              onClick={advance}
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
            >
              {nextCourseId
                ? 'Next course →'
                : nextDayId
                  ? 'Next day →'
                  : 'Finish program preview'}
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="w-[90%] sm:w-[80%] mx-auto pt-8 space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-text-tertiary/10 animate-pulse" />
          ))}
        </div>
      )}

      {isError && (
        <p className="text-sm text-error-600 text-center py-12">Failed to load course preview.</p>
      )}

      {!isLoading && !isError && course && (
        <>
          {/* Inner sticky rows pin just beneath the exit bar above. */}
          <div className="flex flex-col flex-1 min-h-0 [--sticky-top:60px]">
            <CoursePreviewExperience course={course} onExit={() => navigate(-1)} />
          </div>

          {/* What learners are saying, beneath the course itself. Opening a
              course from the library lands here, not in the editor, so this
              is where a trainer looks for it (Aleix, 2026-09-15). */}
          {/* Same measure as the course content above it (Aleix, 2026-09-15). */}
          <div className="w-[95%] sm:w-[90%] mx-auto py-10">
            <CommentsSection targetType="course" targetId={course.id} />
          </div>
        </>
      )}
    </div>
  );
}
